/* Kanban view of the pipeline. Columns are the active stages, in order.
 *  Drag-and-drop is deliberately absent for v1 — it's noisy on a small
 *  founder dataset. Click a card to advance/regress its stage instead. */

import { useEffect, useMemo, useState } from "react";
import { api } from "../../api";

interface Lead {
  id: string;
  company_name: string;
  pipeline_stage: string;
  target_contact_name?: string;
  target_role?: string;
  lead_score?: number;
  updated_at: number;
}

const COLUMNS: Array<{ key: string; label: string }> = [
  { key: "new_lead",              label: "New" },
  { key: "research_needed",       label: "Researching" },
  { key: "enriched",              label: "Enriched" },
  { key: "qualified",             label: "Qualified" },
  { key: "message_drafted",       label: "Drafted" },
  { key: "approved_for_outreach", label: "Approved" },
  { key: "contacted",             label: "Contacted" },
  { key: "follow_up_due",         label: "Follow-up" },
  { key: "replied",               label: "Replied" },
  { key: "call_booked",           label: "Call" },
  { key: "demo_completed",        label: "Demo" },
  { key: "pilot_offered",         label: "Pilot" },
  { key: "trial_active",          label: "Trial" },
  { key: "paid_pilot",            label: "Paid" },
  { key: "customer",              label: "Customer" },
];

export function GrowthPipeline() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const r = await api.listGrowthLeads({ limit: 1000 });
      setLeads((r.leads as Lead[]) ?? []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const out: Record<string, Lead[]> = {};
    for (const c of COLUMNS) out[c.key] = [];
    for (const l of leads) {
      if (out[l.pipeline_stage]) out[l.pipeline_stage].push(l);
    }
    return out;
  }, [leads]);

  async function advance(lead: Lead, dir: 1 | -1) {
    const i = COLUMNS.findIndex((c) => c.key === lead.pipeline_stage);
    if (i < 0) return;
    const next = COLUMNS[i + dir]?.key;
    if (!next) return;
    await api.updateGrowthLead({ id: lead.id, patch: { pipeline_stage: next } });
    load();
  }

  if (loading) return <div className="sg-empty">Loading…</div>;

  return (
    <div className="sg-growth-kanban">
      {COLUMNS.map((c) => (
        <div key={c.key} className="sg-growth-kanban-col">
          <div className="sg-growth-kanban-head">
            <span>{c.label}</span>
            <span className="sg-growth-kanban-count">{grouped[c.key].length}</span>
          </div>
          <div className="sg-growth-kanban-body">
            {grouped[c.key].map((l) => (
              <article key={l.id} className="sg-growth-kanban-card">
                <div className="sg-growth-kanban-card-title">{l.company_name}</div>
                {l.target_contact_name && (
                  <div className="sg-growth-kanban-card-sub">
                    {l.target_contact_name}{l.target_role ? `, ${l.target_role}` : ""}
                  </div>
                )}
                <div className="sg-growth-kanban-card-meta">
                  {typeof l.lead_score === "number" && (
                    <span className="sg-growth-kanban-card-score">{l.lead_score}/10</span>
                  )}
                  <button className="sg-growth-kanban-arrow" title="Move back" onClick={() => advance(l, -1)}>‹</button>
                  <button className="sg-growth-kanban-arrow" title="Move forward" onClick={() => advance(l, 1)}>›</button>
                </div>
              </article>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
